import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Bell, BellOff, Repeat, ListTodo, Paperclip, Lock, Crown, Brain, MessageSquare, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Task, TaskCategory, TaskTag, TaskStatus, TaskRecurrence, TaskReminder, SubTask, TaskAttachment, TaskType, TASK_TYPE_CONFIG, TASK_ICONS, TASK_COLORS } from '@/types/task';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { SubtaskList } from '@/components/SubtaskList';
import { TaskAttachments } from '@/components/TaskAttachments';
import { TagSelector } from '@/components/TagSelector';
import { GoalSelector } from '@/components/goals/GoalSelector';
import { SphereSelector } from '@/components/spheres/SphereSelector';
import { useTranslation } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { getNotificationPermissionStatus } from '@/hooks/useTaskReminders';
import { useAuth } from '@/hooks/useAuth';
import { useUsageLimits } from '@/hooks/useUsageLimits';
import { getOtherMainTasksForDate, type MaybeRecurringTask } from '@/lib/utils/activeTasksForDate';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (task: Omit<Task, 'id' | 'createdAt' | 'completed'>) => void;
  task?: Task | null;
  categories: TaskCategory[];
  tags: TaskTag[];
  onAddCategory?: (category: Omit<TaskCategory, 'id'>) => void;
  onAddTag?: (tag: Omit<TaskTag, 'id'>) => void;
  onRequestNotificationPermission?: () => Promise<boolean>;
  prefillSphereId?: number | null;
  prefillGoalId?: string | null;
  prefillDueDate?: string | null;
  prefillIsMain?: boolean;
}

export function TaskDialog({ open, onClose, onSave, task, categories, tags, onAddCategory, onAddTag, onRequestNotificationPermission, prefillSphereId, prefillGoalId, prefillDueDate, prefillIsMain }: TaskDialogProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { freeFeatureRestrictions, hasProAccess } = useUsageLimits();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(TASK_ICONS[0]);
  const [color, setColor] = useState(TASK_COLORS[0]);
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [status, setStatus] = useState<TaskStatus>('not_started');
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [commonTagIds, setCommonTagIds] = useState<string[]>([]);
  const [recurrence, setRecurrence] = useState<TaskRecurrence>('none');
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState('09:00');
  const [subtasks, setSubtasks] = useState<SubTask[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [goalId, setGoalId] = useState<string | null>(null);
  const [sphereId, setSphereId] = useState<number | null>(null);
  const [sphereLockedByGoal, setSphereLockedByGoal] = useState(false);
  const [taskType, setTaskType] = useState<TaskType | undefined>(undefined);
  const [isMain, setIsMain] = useState(false);
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showNewTag, setShowNewTag] = useState(false);
  const [showSubtasks, setShowSubtasks] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [mainConflict, setMainConflict] = useState<MaybeRecurringTask[] | null>(null);
  const { t, language } = useTranslation();
  const isRussian = language === 'ru';

  // PRO feature blocker component
  const ProFeatureBlock = ({ feature }: { feature: string }) => (
    <div className="mt-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
      <div className="flex items-center gap-2 text-sm">
        <Lock className="w-4 h-4 text-primary" />
        <span className="text-muted-foreground">
          {isRussian 
            ? `${feature} доступны только в PRO` 
            : `${feature} available in PRO only`}
        </span>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="mt-2 w-full gap-2 border-primary/30 text-primary hover:bg-primary/10"
        onClick={() => navigate('/upgrade')}
      >
        <Crown className="w-4 h-4" />
        {isRussian ? 'Перейти на PRO' : 'Upgrade to PRO'}
      </Button>
    </div>
  );

  useEffect(() => {
    if (task) {
      setName(task.name);
      setIcon(task.icon);
      setColor(task.color);
      setDueDate(task.dueDate);
      setPriority(task.priority);
      setStatus(task.status);
      setCategoryId(task.categoryId);
      const localTagIdSet = new Set(tags.map(t => t.id));
      setTagIds(task.tagIds.filter(id => localTagIdSet.has(id)));
      setCommonTagIds(task.tagIds.filter(id => !localTagIdSet.has(id)));
      setRecurrence(task.recurrence || 'none');
      setReminderEnabled(task.reminder?.enabled || false);
      setReminderTime(task.reminder?.time || '09:00');
      setSubtasks(task.subtasks || []);
      setAttachments(task.attachments || []);
      setNotes(task.notes || '');
      setGoalId((task as any).goalId || null);
      setSphereId((task as any).sphereId ?? null);
      setSphereLockedByGoal(!!(task as any).goalId);
      setTaskType(task.taskType);
      setIsMain(task.isMain || false);
      setDuration(task.duration);
      setShowSubtasks((task.subtasks?.length || 0) > 0);
      setShowAttachments((task.attachments?.length || 0) > 0 || !!task.notes);
    } else {
      setName('');
      setIcon(TASK_ICONS[0]);
      setColor(TASK_COLORS[0]);
      setDueDate(prefillDueDate || new Date().toISOString().split('T')[0]);
      setPriority('medium');
      setStatus('not_started');
      setCategoryId(undefined);
      setTagIds([]);
      setCommonTagIds([]);
      setRecurrence('none');
      setReminderEnabled(false);
      setReminderTime('09:00');
      setSubtasks([]);
      setAttachments([]);
      setNotes('');
      setGoalId(prefillGoalId || null);
      setSphereId(prefillSphereId ?? null);
      setSphereLockedByGoal(!!prefillGoalId);
      setTaskType(undefined);
      setIsMain(!!prefillIsMain);
      setDuration(undefined);
      setShowSubtasks(false);
      setShowAttachments(false);
    }
  }, [task, open, tags, prefillSphereId, prefillGoalId, prefillDueDate, prefillIsMain]);

  const handleSave = () => {
    if (!name.trim()) return;
    // Require sphere for authenticated users
    if (user && !sphereId) return;
    const reminder: TaskReminder | undefined = reminderEnabled 
      ? { enabled: true, time: reminderTime }
      : undefined;
    const allTagIds = [...tagIds, ...commonTagIds];
    onSave({ 
      name: name.trim(), icon, color, dueDate, priority, status, 
      categoryId, tagIds: allTagIds, recurrence, reminder, subtasks, attachments, notes,
      goalId, sphereId, taskType, isMain, duration,
    } as any);
    onClose();
  };

  const handleGoalChange = (newGoalId: string | null, goalSphereId?: number | null) => {
    setGoalId(newGoalId);
    if (newGoalId && goalSphereId !== undefined) {
      // Inherit sphere from goal
      setSphereId(goalSphereId);
      setSphereLockedByGoal(true);
    } else {
      // No goal - unlock sphere selector
      setSphereLockedByGoal(false);
    }
  };

  const handleReminderToggle = async (enabled: boolean) => {
    if (enabled) {
      const permissionStatus = getNotificationPermissionStatus();
      if (permissionStatus === 'unsupported') {
        return;
      }
      if (permissionStatus !== 'granted' && onRequestNotificationPermission) {
        const granted = await onRequestNotificationPermission();
        if (!granted) return;
      }
    }
    setReminderEnabled(enabled);
  };

  const handleAddCategory = () => {
    if (newCategoryName.trim() && onAddCategory) {
      onAddCategory({ name: newCategoryName.trim(), color: TASK_COLORS[Math.floor(Math.random() * TASK_COLORS.length)] });
      setNewCategoryName('');
      setShowNewCategory(false);
    }
  };

  const handleAddTag = () => {
    if (newTagName.trim() && onAddTag) {
      onAddTag({ name: newTagName.trim(), color: TASK_COLORS[Math.floor(Math.random() * TASK_COLORS.length)] });
      setNewTagName('');
      setShowNewTag(false);
    }
  };

  const toggleTag = (id: string) => {
    setTagIds(prev => 
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleAttachmentsChange = (newAttachments: TaskAttachment[], newNotes?: string) => {
    setAttachments(newAttachments);
    if (newNotes !== undefined) setNotes(newNotes);
  };

  // Main-task conflict handling — only ONE main task per day
  const tryToggleMain = (next: boolean) => {
    if (!next) {
      setIsMain(false);
      return;
    }
    try {
      const stored = localStorage.getItem('habitflow_tasks');
      const tasks: MaybeRecurringTask[] = stored ? JSON.parse(stored) : [];
      const conflicts = getOtherMainTasksForDate(tasks, dueDate, task?.id);
      if (conflicts.length > 0) {
        setMainConflict(conflicts);
        return;
      }
    } catch {/* noop */}
    setIsMain(true);
  };

  const confirmReplaceMain = () => {
    try {
      const stored = localStorage.getItem('habitflow_tasks');
      if (stored) {
        const tasks = JSON.parse(stored);
        const updated = tasks.map((t: any) =>
          mainConflict?.some((c) => c.id === t.id) ? { ...t, isMain: false } : t,
        );
        localStorage.setItem('habitflow_tasks', JSON.stringify(updated));
        window.dispatchEvent(new CustomEvent('habitflow-data-changed'));
      }
    } catch {/* noop */}
    setIsMain(true);
    setMainConflict(null);
  };

  const priorities: Array<{ value: 'low' | 'medium' | 'high'; label: string }> = [
    { value: 'low', label: t('priorityLow') },
    { value: 'medium', label: t('priorityMedium') },
    { value: 'high', label: t('priorityHigh') },
  ];

  const statuses: Array<{ value: TaskStatus; label: string }> = [
    { value: 'not_started', label: t('statusNotStarted') },
    { value: 'in_progress', label: t('statusInProgress') },
    { value: 'done', label: t('statusDone') },
  ];

  const recurrenceOptions: Array<{ value: TaskRecurrence; label: string }> = [
    { value: 'none', label: t('recurrenceNone') },
    { value: 'daily', label: t('recurrenceDaily') },
    { value: 'weekly', label: t('recurrenceWeekly') },
    { value: 'monthly', label: t('recurrenceMonthly') },
  ];

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-x-4 top-[5%] bottom-24 max-w-md mx-auto bg-card rounded-3xl p-6 shadow-lg z-50 overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">
                {task ? t('editTask') : t('newTask')}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Name Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('taskName')}
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('taskNamePlaceholder')}
                className="bg-background border-border"
              />
            </div>

            {/* Due Date */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('dueDate')}
              </label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="bg-background border-border"
              />
            </div>

            {/* Goal Selector */}
            {(user || prefillGoalId || prefillSphereId) && (
              <div className="mb-4">
                <GoalSelector
                  value={goalId}
                  onChange={handleGoalChange}
                  isRussian={isRussian}
                />
              </div>
            )}

            {/* Sphere Selector */}
            {(user || prefillGoalId || prefillSphereId) && (
              <div className="mb-4">
                <SphereSelector
                  value={sphereId}
                  onChange={setSphereId}
                  disabled={sphereLockedByGoal}
                  showWarning={!sphereId && sphereId !== 0}
                />
                {sphereLockedByGoal && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {isRussian 
                      ? 'Сфера унаследована от цели'
                      : 'Sphere inherited from goal'}
                  </p>
                )}
              </div>
            )}

            {/* Task Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                {isRussian ? 'Тип задачи' : 'Task Type'}
              </label>
              <div className="flex gap-2 flex-wrap">
                {(Object.keys(TASK_TYPE_CONFIG) as TaskType[]).map(type => {
                  const cfg = TASK_TYPE_CONFIG[type];
                  const isSelected = taskType === type;
                  const TypeIcon = type === 'intellectual' ? Brain : type === 'routine' ? Repeat : type === 'communication' ? MessageSquare : Zap;
                  return (
                    <button
                      key={type}
                      onClick={() => setTaskType(isSelected ? undefined : type)}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all border",
                        isSelected
                          ? "border-transparent ring-1 shadow-sm"
                          : "border-border bg-muted text-muted-foreground hover:bg-muted/80"
                      )}
                      style={isSelected ? { backgroundColor: cfg.color + '20', color: cfg.color, borderColor: cfg.color } : undefined}
                    >
                      <TypeIcon className="w-3.5 h-3.5" />
                      {isRussian ? cfg.label : cfg.labelEn}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Main Task + Duration */}
            <div className="mb-4 flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isMain}
                  onChange={e => tryToggleMain(e.target.checked)}
                  className="rounded border-border"
                />
                <span className="text-sm font-medium text-foreground">
                  🎯 {isRussian ? 'Главная задача' : 'Main task'}
                </span>
              </label>
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">{isRussian ? 'Мин:' : 'Min:'}</label>
                <Input
                  type="number"
                  min={0}
                  max={480}
                  value={duration || ''}
                  onChange={e => setDuration(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-20 h-8 bg-background border-border text-sm"
                  placeholder="30"
                />
              </div>
            </div>


            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('priority')}
              </label>
              <div className="flex gap-2">
                {priorities.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => setPriority(p.value)}
                    className={cn(
                      "flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-all",
                      priority === p.value
                        ? "bg-task text-white"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Status */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('status')}
              </label>
              <div className="flex gap-2">
                {statuses.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setStatus(s.value)}
                    className={cn(
                      "flex-1 py-2 px-3 rounded-xl text-xs font-medium transition-all",
                      status === s.value
                        ? "bg-task text-white"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Recurrence */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                <Repeat className="w-4 h-4 inline mr-1" />
                {t('recurrence')}
                {!freeFeatureRestrictions.recurrenceEnabled && (
                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">PRO</span>
                )}
              </label>
              {freeFeatureRestrictions.recurrenceEnabled ? (
                <div className="flex gap-2 flex-wrap">
                  {recurrenceOptions.map((r) => (
                    <button
                      key={r.value}
                      onClick={() => setRecurrence(r.value)}
                      className={cn(
                        "py-2 px-3 rounded-xl text-xs font-medium transition-all",
                        recurrence === r.value
                          ? "bg-task text-white"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      )}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              ) : (
                <ProFeatureBlock feature={isRussian ? 'Повторяющиеся задачи' : 'Recurring tasks'} />
              )}
            </div>

            {/* Reminder */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                {reminderEnabled ? <Bell className="w-4 h-4 inline mr-1" /> : <BellOff className="w-4 h-4 inline mr-1" />}
                {t('reminder')}
              </label>
              <div className="flex items-center gap-3">
                <Switch
                  checked={reminderEnabled}
                  onCheckedChange={handleReminderToggle}
                />
                {reminderEnabled && (
                  <Input
                    type="time"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    className="w-32 bg-background border-border"
                  />
                )}
                <span className="text-sm text-muted-foreground">
                  {reminderEnabled ? t('reminderEnabled') : t('reminderDisabled')}
                </span>
              </div>
            </div>

            {/* Subtasks toggle and list */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => freeFeatureRestrictions.subtasksEnabled && setShowSubtasks(!showSubtasks)}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium transition-colors",
                  !freeFeatureRestrictions.subtasksEnabled && "opacity-60 cursor-not-allowed",
                  showSubtasks ? "text-task" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <ListTodo className="w-4 h-4" />
                {t('subtasks')} {subtasks.length > 0 && `(${subtasks.length})`}
                {!freeFeatureRestrictions.subtasksEnabled && (
                  <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">PRO</span>
                )}
              </button>
              {freeFeatureRestrictions.subtasksEnabled ? (
                showSubtasks && (
                  <div className="mt-2 pl-2 border-l-2 border-task/30">
                    <SubtaskList subtasks={subtasks} onChange={setSubtasks} />
                  </div>
                )
              ) : (
                <ProFeatureBlock feature={isRussian ? 'Подзадачи' : 'Subtasks'} />
              )}
            </div>

            {/* Attachments toggle and section */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => freeFeatureRestrictions.attachmentsEnabled && setShowAttachments(!showAttachments)}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium transition-colors",
                  !freeFeatureRestrictions.attachmentsEnabled && "opacity-60 cursor-not-allowed",
                  showAttachments ? "text-task" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Paperclip className="w-4 h-4" />
                {t('attachments')} {attachments.length > 0 && `(${attachments.length})`}
                {!freeFeatureRestrictions.attachmentsEnabled && (
                  <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">PRO</span>
                )}
              </button>
              {freeFeatureRestrictions.attachmentsEnabled ? (
                showAttachments && (
                  <div className="mt-2 pl-2 border-l-2 border-task/30">
                    <TaskAttachments 
                      attachments={attachments} 
                      notes={notes}
                      onChange={handleAttachmentsChange}
                    />
                  </div>
                )
              ) : (
                <ProFeatureBlock feature={isRussian ? 'Вложения' : 'Attachments'} />
              )}
            </div>

            {/* Category */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('category')}
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => {
                  const isSelected = categoryId === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setCategoryId(isSelected ? undefined : cat.id)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-sm font-medium transition-all border-2",
                        isSelected
                          ? "ring-2 ring-offset-1 ring-offset-card shadow-sm"
                          : "opacity-80 hover:opacity-100"
                      )}
                      style={{ 
                        backgroundColor: 'transparent',
                        color: cat.color,
                        borderColor: cat.color
                      }}
                    >
                      {cat.name}
                    </button>
                  );
                })}
                {showNewCategory ? (
                  <div className="flex gap-1">
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="..."
                      className="h-7 w-24 text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                    />
                    <Button size="sm" onClick={handleAddCategory} className="h-7 px-2">
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewCategory(true)}
                    className="px-3 py-1 rounded-full text-sm bg-muted text-muted-foreground hover:bg-muted/80"
                  >
                    + {t('addCategory')}
                  </button>
                )}
              </div>
            </div>

            {/* Tags */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('tagsLabel')}
              </label>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const isSelected = tagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.id)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-sm font-medium transition-all border-2",
                        isSelected
                          ? "ring-2 ring-offset-1 ring-offset-card shadow-sm"
                          : "opacity-80 hover:opacity-100"
                      )}
                      style={{ 
                        backgroundColor: 'transparent',
                        color: tag.color,
                        borderColor: tag.color
                      }}
                    >
                      {tag.name}
                    </button>
                  );
                })}
                {showNewTag ? (
                  <div className="flex gap-1">
                    <Input
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="..."
                      className="h-7 w-24 text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                    />
                    <Button size="sm" onClick={handleAddTag} className="h-7 px-2">
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewTag(true)}
                    className="px-3 py-1 rounded-full text-sm bg-muted text-muted-foreground hover:bg-muted/80"
                  >
                    + {t('addTag')}
                  </button>
                )}
              </div>
            </div>

            {/* Common Tags (from profile) */}
            {user && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('commonTags')}
                </label>
                <TagSelector 
                  selectedTagIds={commonTagIds} 
                  onChange={setCommonTagIds} 
                />
              </div>
            )}

            {/* Icon Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('icon')}
              </label>
              <div className="grid grid-cols-8 gap-2">
                {TASK_ICONS.map((i) => (
                  <button
                    key={i}
                    onClick={() => setIcon(i)}
                    className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center text-xl transition-all",
                      icon === i
                        ? "bg-task/20 ring-2 ring-task"
                        : "bg-muted hover:bg-muted/80"
                    )}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('color')}
              </label>
              <div className="flex gap-2 flex-wrap">
                {TASK_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={cn(
                      "w-8 h-8 rounded-full transition-all",
                      color === c && "ring-2 ring-offset-2 ring-offset-card ring-task"
                    )}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={!name.trim()}
              className="w-full bg-task hover:bg-task/90 text-white"
            >
              {t('save')}
            </Button>
          </motion.div>
        </>
      )}
      <AlertDialog open={!!mainConflict} onOpenChange={(o) => !o && setMainConflict(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isRussian ? '🎯 Только одна Главная задача в день' : '🎯 Only one Main Task per day'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isRussian
                ? 'На эту дату уже назначена Главная задача. Активные задачи дня:'
                : 'A Main Task is already set for this date. Active tasks today:'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-[200px] overflow-y-auto space-y-1 my-2">
            {mainConflict?.map((t) => (
              <div key={t.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 text-sm">
                <span>{t.icon || '📝'}</span>
                <span className="flex-1 truncate">{t.name}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                  🎯 {isRussian ? 'Главная' : 'Main'}
                </span>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{isRussian ? 'Отмена' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReplaceMain}>
              {isRussian ? 'Сделать эту главной' : 'Make this the Main'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AnimatePresence>
  );
}
